/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Route, Switch } from 'react-router-dom';

import { GlobalStyle } from '../styles/global-styles';
import { NotFoundPage } from './pages/NotFoundPage/Loadable';
import { useTranslation } from 'react-i18next';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import PublicRoute from '../utils/PublicRoute';
import PrivateRoute from '../utils/PrivateRoute';
import { ContractDetailPage } from './pages/ContractDetailPage';
import { InteractionStatus } from '@azure/msal-browser';
import { WorkListPage } from './pages/WorkListPage';
import { useWorkListStateSlice } from './pages/WorkListPage/slice';
import { useDispatch } from 'react-redux';
import { UseAxios } from '../utils/UseAxios';
import { apiList } from '../locales/apiList';
import ReportPage from './pages/ReportPage';

export function App() {
  const { i18n } = useTranslation();
  const { instance, accounts, inProgress } = useMsal();
  const { actions } = useWorkListStateSlice();
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();

  // Set active account immediately when available
  useEffect(() => {
    const currentAccount = instance.getActiveAccount();
    if (accounts.length > 0 && !currentAccount) {
      // Set the first account as active if none is set
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  // Handle authentication
  useEffect(() => {
    let isSubscribed = true;

    const handleAuth = async () => {
      // Don't proceed if there's already an interaction in progress
      if (inProgress !== InteractionStatus.None) {
        console.log('Auth interaction already in progress, skipping...');
        return;
      }

      try {
        const activeAccount = instance.getActiveAccount();
        
        // If no active account, trigger login
        if (!activeAccount) {
          console.log('No active account, redirecting to login...');
          instance.loginRedirect({
            scopes: ['user.read'],
          });
          return;
        }

        // Check if token is expired
        const idTokenClaims = activeAccount.idTokenClaims as any;
        const isExpired = Number(idTokenClaims?.exp) * 1000 <= Date.now();

        if (isExpired) {
          console.log('Token expired, acquiring new token...');
          instance.loginRedirect({
            scopes: ['user.read'],
            account: activeAccount,
          });
          return;
        }

        // Try silent token acquisition
        try {
          const token = await instance.acquireTokenSilent({
            scopes: ['user.read'],
            account: activeAccount,
          });

          if (isSubscribed && token) {
            dispatch(actions.updateUsername(activeAccount.name || ''));
            dispatch(actions.updateUserEmail(activeAccount.username || ''));
            await getUserProfile();
          }
        } catch (silentError) {
          console.error('Silent token acquisition failed:', silentError);
          // Only redirect if we're not in an interaction and component is still mounted
          if (inProgress === InteractionStatus.None && isSubscribed) {
            instance.loginRedirect({
              scopes: ['user.read'],
              account: activeAccount,
            });
          }
        }
      } catch (e) {
        console.error('Authentication error:', e);
      }
    };

    handleAuth();

    return () => {
      isSubscribed = false;
    };
  }, [inProgress]); // Only depend on inProgress

  const getUserProfile = async () => {
    const result = await UseAxios(
      `${apiList.geteBillUserProfile}`,
      'get',
      null,
      true,
    );
    if (result.data) {
      dispatch(actions.updateDept(result.data.user.department.name));
      result.data.accessControlList.forEach(access => {
        if (access['code'] === 'WEB_BI') {
          dispatch(actions.updateAccessRight(access));
        }
      });
    }
  };

  return (
    <BrowserRouter>
      <Helmet
        titleTemplate="WEB-BI"
        defaultTitle="WEB-BI"
        htmlAttributes={{ lang: i18n.language }}
      >
        <meta name="description" content="WEB-BI" />
      </Helmet>
      <Switch>
        <PublicRoute component={LoginPage} path="/login" exact />
        <PublicRoute component={LoginPage} path="/auth" exact />
        <PrivateRoute
          component={WorkListPage}
          path="/"
          exact
          restricted={true}
        />
        <PrivateRoute
          component={ContractDetailPage}
          path="/detail/:id"
          exact
          restricted={false}
        />
        <PrivateRoute
          component={ContractDetailPage}
          path="/detail/:id/:openChatter"
          exact
          restricted={false}
        />
        <PrivateRoute
          component={HomePage}
          path="/boilerplate"
          exact
          restricted={true}
        />
        <PrivateRoute
          component={ReportPage}
          path="/report"
          exact
          restricted={false}
        />
        <Route component={NotFoundPage} />
      </Switch>
      <GlobalStyle />
    </BrowserRouter>
  );
}
